import { useQuery } from "@tanstack/react-query";
import { getNationRivalry } from "@/lib/social.functions";
import { useLanguage } from "@/hooks/useLanguage";
import { Globe2 } from "lucide-react";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { OFFICIAL_MATCH } from "@/lib/officialMatch";

const FLAGS: Record<string, string> = {
  MEX: "🇲🇽", ARG: "🇦🇷", BRA: "🇧🇷", FRA: "🇫🇷", ESP: "🇪🇸", USA: "🇺🇸",
  ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", GER: "🇩🇪", ITA: "🇮🇹", POR: "🇵🇹", URU: "🇺🇾", COL: "🇨🇴",
  JPN: "🇯🇵", KOR: "🇰🇷", NED: "🇳🇱", BEL: "🇧🇪", CRO: "🇭🇷", MAR: "🇲🇦",
};

export function NationRivalryWidget() {
  const { t } = useLanguage();
  const q = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 20_000,
  });
  const rows = q.data?.rows ?? [];
  // PREVIEW MODE — force the podium to the OFFICIAL_MATCH pair
  // (España vs Argentina) so no residual dataset (e.g. Brasil #2)
  // contradicts the Single Source of Truth.
  const displayRows = PREVIEW_MODE
    ? [
        { nation_code: OFFICIAL_MATCH.homeCode, hype_total: 12800, active_fans: 8600 },
        { nation_code: OFFICIAL_MATCH.awayCode, hype_total: 11450, active_fans: 8020 },
      ]
    : rows;
  const max = Math.max(1, ...displayRows.map((r) => Number(r.hype_total ?? 0)));

  return (
    <section className="rounded-2xl border border-border bg-background/60 p-4">
      <header className="mb-3 flex items-center gap-2">
        <Globe2 className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-black uppercase tracking-wider">{t("nation_wars_title")}</h3>
      </header>
      {displayRows.length === 0 && (
        <p className="text-xs text-muted-foreground">{t("nation_wars_empty")}</p>
      )}
      <ul className="space-y-2">
        {displayRows.slice(0, 8).map((r) => {
          const total = Number(r.hype_total ?? 0);
          const pct = Math.round((total / max) * 100);
          const flag = FLAGS[r.nation_code as string] ?? "🏳️";
          return (
            <li key={r.nation_code as string} className="space-y-1">
              <div className="flex items-center justify-between text-xs font-bold">
                <span>{flag} {r.nation_code}</span>
                <span className="font-mono tabular-nums text-muted-foreground">
                  {total} · {Number(r.active_fans ?? 0)} {t("nation_wars_fans")}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary/60">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: "linear-gradient(90deg, oklch(0.78 0.2 60), oklch(0.65 0.27 27))" }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}